import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useFloorPlanRoomsCrud } from "../useFloorPlanRoomsCrud";
import { useRoomsCreation } from "../useRoomsCreation";
import { useRoomInspector } from "../useRoomInspector";
import { useRoomsMergeSelection } from "../useRoomsMergeSelection";
import { ACTIVE_PAGE_INDEX } from "../useSelectedRoom/constants";
import { getWallStrokeWidthPt } from "../../utils/floorPlanLayout";
import { MIN_POLYGON_POINTS } from "../../utils/polygonPointMutators";
import { findRoomAtPoint, getMergedRoomOutline } from "../../utils/rooms";
import type { PolygonPoint } from "../../utils/polygonPointMutators";
import type {
  MergeRoomsSelection,
  UseRoomsEditorArgs,
  UseRoomsEditorResult,
} from "./types";

export const useRoomsEditor = ({
  selectedPlan,
  rooms,
  interactionMode,
  startDrawingRooms,
  setRoomsStep,
  startEditingRoom,
  stopRooms,
  startMergingRooms,
  stopMergingRooms,
  mergeableRoomIds = null,
  floorPlanPanelHandlers,
}: UseRoomsEditorArgs): UseRoomsEditorResult => {
  const { t } = useTranslation();

  const isDrawingRooms = interactionMode.kind === "drawing-rooms";
  const drawingStep = isDrawingRooms ? interactionMode.step : null;
  const editedRoomId =
    interactionMode.kind === "editing-room" ? interactionMode.roomId : null;

  const pageRooms = useMemo(
    () => rooms.filter((room) => room.pageIndex === ACTIVE_PAGE_INDEX),
    [rooms],
  );

  const editedRoom = useMemo(
    () => pageRooms.find((room) => room.id === editedRoomId) ?? null,
    [pageRooms, editedRoomId],
  );

  const floorPlanId = selectedPlan?.floorPlanId;
  const pageId = selectedPlan?.pageIdByIndex.get(ACTIVE_PAGE_INDEX);
  const wallWidthPt = getWallStrokeWidthPt({
    metersPerPdfPoint:
      selectedPlan?.metersPerPdfPointByPageIndex.get(ACTIVE_PAGE_INDEX) ?? 0,
  });

  const {
    isCreatingRooms,
    createRooms,
    mergeRooms: saveMergedRoom,
    updateRoomGeometry,
    renameRoom,
    deleteRoom,
  } = useFloorPlanRoomsCrud({ floorPlanId, rooms: pageRooms });

  const creation = useRoomsCreation({
    isActive: isDrawingRooms,
    step: drawingStep,
    pageRooms,
    pageId,
    pageIndex: ACTIVE_PAGE_INDEX,
    isSaving: isCreatingRooms,
    createRooms,
    setStep: setRoomsStep,
    stop: stopRooms,
    floorPlanPanelHandlers,
  });

  const inspector = useRoomInspector({
    room: editedRoom,
    pageRooms,
    renameRoom,
    updateRoomGeometry,
    deleteRoom,
    stop: stopRooms,
  });

  // Both start from the list, where a room may still be open for editing; that edit is dropped.
  const startCreating = useCallback(() => {
    stopRooms();
    startDrawingRooms({ pageIndex: ACTIVE_PAGE_INDEX });
  }, [stopRooms, startDrawingRooms]);

  const startEditing = useCallback(
    (roomId: number) => {
      const room = pageRooms.find((candidate) => candidate.id === roomId);
      if (!room) return;
      stopRooms();
      startEditingRoom({ pageIndex: ACTIVE_PAGE_INDEX, roomId });
      floorPlanPanelHandlers?.current?.zoomToElements({
        elements: room.points,
      });
    },
    [pageRooms, stopRooms, startEditingRoom, floorPlanPanelHandlers],
  );

  const mergeRooms = useCallback(
    async ({ roomIds, name }: MergeRoomsSelection) => {
      if (pageId === undefined) {
        toast.error(t("smartSurveyor.rooms.mergeFailed"));
        return false;
      }
      const points = getMergedRoomOutline({
        rooms: pageRooms,
        roomIds,
        wallWidthPt,
      });
      if (points.length < MIN_POLYGON_POINTS) {
        toast.error(t("smartSurveyor.rooms.mergeFailed"));
        return false;
      }
      const mergedRoom = await saveMergedRoom({
        pageId,
        pageIndex: ACTIVE_PAGE_INDEX,
        roomIds,
        name: name.trim(),
        points,
      });
      return mergedRoom !== null;
    },
    [pageId, pageRooms, wallWidthPt, saveMergedRoom, t],
  );

  const startMerging = useCallback(() => {
    startMergingRooms({ pageIndex: ACTIVE_PAGE_INDEX });
  }, [startMergingRooms]);

  const isMergingRooms = interactionMode.kind === "merging-rooms";

  // Inside a zone only its rooms are listed and drawn, so only they can be ticked, from the plan too.
  const mergeableRooms = useMemo(
    () =>
      mergeableRoomIds === null
        ? pageRooms
        : pageRooms.filter((room) => mergeableRoomIds.has(room.id)),
    [pageRooms, mergeableRoomIds],
  );

  const merge = useRoomsMergeSelection({
    rooms: mergeableRooms,
    isSelecting: isMergingRooms,
    startSelecting: startMerging,
    stopSelecting: stopMergingRooms,
    onMergeRooms: mergeRooms,
  });
  const { toggleRoom: toggleMergeRoom } = merge;

  const toggleMergeRoomAt = useCallback(
    (point: PolygonPoint) => {
      const room = findRoomAtPoint({ rooms: mergeableRooms, point });
      if (room) toggleMergeRoom(room);
    },
    [mergeableRooms, toggleMergeRoom],
  );

  // The inspected room left the cache, so there is nothing left to inspect.
  useEffect(() => {
    if (editedRoomId !== null && editedRoom === null) stopRooms();
  }, [editedRoomId, editedRoom, stopRooms]);

  const activeOverlay = isDrawingRooms
    ? creation.overlay
    : inspector.state !== null
      ? inspector.overlay
      : null;

  return {
    startCreating,
    startEditing,
    merge,
    rooms: pageRooms,
    creation: isDrawingRooms ? creation.state : null,
    inspector: inspector.state,
    overlay: activeOverlay,
    isRoomEditingActive: creation.isEditingActive || inspector.state !== null,
    handleMapClick: isMergingRooms
      ? toggleMergeRoomAt
      : creation.handleMapClick,
    handleMapMouseMove: isDrawingRooms
      ? creation.handleMapMouseMove
      : inspector.handleMapMouseMove,
    requestClose: isDrawingRooms ? creation.state.requestLeave : stopRooms,
  };
};
